import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { EASE } from './SetupStart';

/**
 * THE MOMENT OF HAVING BUILT IT.
 *
 * Creating a page used to fire a toast and drop you into the admin screen.
 * Tom: *"it should finish the way it opened — full screen, the client's
 * link, Copy, and Preview as client. You've just built something; there's
 * no moment of having built it."*
 *
 * The link is the only thing on this screen that matters, so it is the only
 * thing on it.
 */
export function SetupDone({
  client,
  name,
  slug,
  token,
  trouble,
  onOpen,
  onClose
}: {
  client: string;
  name: string;
  slug: string;
  token: string;
  /** What did not save, if anything. Said plainly rather than swallowed. */
  trouble?: string;
  onOpen: () => void;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const link = slug ? `https://clients.portalproductions.me/project/${slug}${token ? `?access=${token}` : ''}` : '';
  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      setCopied(false);
    }
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.3,
    ease: EASE
  }} className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-ink text-paper pt-safe" data-id="element-1297" data-mp="1ahllzyoi7ijf">
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(120% 90% at 10% 0%, rgba(244,241,234,0.10) 0%, rgba(244,241,234,0.03) 40%, rgba(244,241,234,0) 70%)'
    }} data-id="element-1298" data-mp="2876pg6u9j1yh" />

      <div className="relative flex min-h-full flex-col px-6 py-7 sm:px-12 sm:py-9" data-id="element-1299" data-mp="1yb3q18qsphf8">
        <div className="flex items-baseline justify-between gap-6" data-id="element-1300" data-mp="iuldljkgavjf">
          <span className="label" data-id="element-1301" data-mp="muvjyxc5nnyt">{client}</span>
          <button type="button" onClick={onClose} className="label text-paper/45 transition-colors duration-150 hover:text-paper" data-id="element-1302" data-mp="2pgreukzq90as">
            Close
          </button>
        </div>

        <div className="flex flex-1 items-center py-16" data-id="element-1303" data-mp="2fq698dlmyvid">
          <div className="w-full max-w-4xl" data-id="element-1304" data-mp="2folcchbun0hc">
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: EASE,
            delay: 0.05
          }} className="label m-0 text-mute" data-id="element-1305" data-mp="2asmrh3pqc0lo">
              Their page is up
            </motion.p>

            <motion.h1 initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: EASE,
            delay: 0.11
          }} className="mt-6 m-0 font-display text-[2.6rem] font-extralight leading-[0.92] tracking-tight sm:text-[4rem] lg:text-[5rem]" data-id="element-1306" data-mp="1wrahra15wv88">
              {name}
            </motion.h1>

            {link && <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: EASE,
            delay: 0.18
          }} className="mt-12 max-w-2xl border-t border-ruleDark pt-6" data-id="element-1307" data-mp="3umzg8bnzdire">
                <p className="label m-0 text-paper/40" data-id="element-1308" data-mp="cab01aln40uh">Their link</p>
                <p className="mt-3 m-0 break-all text-[1.05rem] font-light leading-relaxed text-paper/75" data-id="element-1309" data-mp="3bp6al1lz2uqb">
                  {link}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-5" data-id="element-1310" data-mp="3frjh3s8jmwo5">
                  <button type="button" onClick={() => void copy()} className="label group inline-flex items-center gap-4 border border-paper bg-paper px-8 py-4 text-ink transition-colors duration-200 ease-out hover:bg-transparent hover:text-paper" data-id="element-1311" data-mp="3kpl9x4vruact">
                    {copied ? 'Copied' : 'Copy the link'}
                    {copied && <CheckIcon className="h-3.5 w-3.5" strokeWidth={2} data-id="element-1312" data-mp="2duvjtw7qhz4w" />}
                  </button>

                  <a href={link} target="_blank" rel="noopener noreferrer" className="label group inline-flex items-center gap-3 border border-paper/30 px-7 py-4 text-paper/70 no-underline transition-colors duration-200 hover:border-paper hover:text-paper" data-id="element-1313" data-mp="28xc7j1uljwfl">
                    Preview as client
                    <ArrowRightIcon className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} data-id="element-1314" data-mp="2rvy5w8yqb9ft" />
                  </a>
                </div>

                <p className="mt-7 m-0 text-[0.85rem] font-light leading-relaxed text-paper/35" data-id="element-1315" data-mp="31m1a75baw9tg">
                  Nobody has it until you send it.
                </p>
              </motion.div>}

            {trouble && <p className="mt-8 m-0 max-w-xl border-l-2 border-paper/40 pl-5 text-[0.9rem] font-light leading-relaxed text-paper/55" data-id="element-1316" data-mp="3u7zcn54qzklv">
                {trouble}
              </p>}
          </div>
        </div>

        <div className="border-t border-ruleDark pt-6 pb-safe" data-id="element-1317" data-mp="3kabhwf756d3p">
          <button type="button" onClick={onOpen} className="label group inline-flex items-center gap-3 text-paper/50 transition-colors duration-200 hover:text-paper" data-id="element-1318" data-mp="3kbwf6ckuca59">
            Open it in the studio
            <ArrowRightIcon className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} data-id="element-1319" data-mp="3g8zxco34ki2d" />
          </button>
        </div>
      </div>
    </motion.div>;
}